import { keys } from '@material-ui/core/styles/createBreakpoints';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type { Optional } from 'utility-types';

/**
 * Per https://legacydocs.hubspot.com/docs/methods/forms/submit_form
 */
enum SubmitFormErrorCodes {
  /**
   * More than 1000 fields were included in the response.
   */
  MAX_NUMBER_OF_SUBMITTED_VALUES_EXCEEDED = 'MAX_NUMBER_OF_SUBMITTED_VALUES_EXCEEDED',
  /**
   * For email fields, the email was not a valid format.
   */
  INVALID_EMAIL = 'INVALID_EMAIL',
  /**
   * For email fields, the email is blocked by the blocked email list for the form.
   */
  BLOCKED_EMAIL = 'BLOCKED_EMAIL',
  /**
   * The field is marked as required but was not included in the submission.
   */
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  /**
   * For number fields, the value was not a number value.
   */
  INVALID_NUMBER = 'INVALID_NUMBER',

  /**
   * The value in the field is too large for the type of field.
   */
  INPUT_TOO_LARGE = 'INPUT_TOO_LARGE',

  /**
   * The field was included in the form submission but is not in the form definition.
   */
  FIELD_NOT_IN_FORM_DEFINITION = 'FIELD_NOT_IN_FORM_DEFINITION',

  /**
   * The value of a number field outside the range specified in the field settings.
   */
  NUMBER_OUT_OF_RANGE = 'NUMBER_OUT_OF_RANGE',

  /**
   * The value provided for an enumeration field (e.g. checkbox, dropdown, radio) is not one of the possible options.
   */
  VALUE_NOT_IN_FIELD_DEFINITION = 'VALUE_NOT_IN_FIELD_DEFINITION',
  /**
   * The context object contains an unexpected attribute.
   */
  INVALID_METADATA = 'INVALID_METADATA',

  /**
   * The value in goToWebinarWebinarKey in the context object is invalid.
   */
  INVALID_GOTOWEBINAR_WEBINAR_KEY = 'INVALID_GOTOWEBINAR_WEBINAR_KEY',
  /**
   * The hutk field in the context object is invalid.
   */
  INVALID_HUTK = 'INVALID_HUTK',

  /**
   * The ipAddress field in the context object is invalid.
   */
  INVALID_IP_ADDRESS = 'INVALID_IP_ADDRESS',
  /**
   * The pageUri field in the context object is invalid.
   */
  INVALID_PAGE_URI = 'INVALID_PAGE_URI',

  /**
   * legalConsentOptions was empty or it contains both the consent and legitimateInterest fields.
   */
  INVALID_LEGAL_OPTION_FORMAT = 'INVALID_LEGAL_OPTION_FORMAT',

  /**
   * The consentToProcess field in consent or value field in legitimateInterest was false.
   */
  MISSING_PROCESSING_CONSENT = 'MISSING_PROCESSING_CONSENT',
  /**
   * The text field for processing consent was missing.
   */
  MISSING_PROCESSING_CONSENT_TEXT = 'MISSING_PROCESSING_CONSENT_TEXT',

  /**
   * The communication consent text was missing for a subscription.
   */
  MISSING_COMMUNICATION_CONSENT_TEXT = 'MISSING_COMMUNICATION_CONSENT_TEXT',
  /**
   * The legitimate interest consent text was missing.
   */
  MISSING_LEGITIMATE_INTEREST_TEXT = 'MISSING_LEGITIMATE_INTEREST_TEXT',

  /**
   * The communications list contains two or more items with the same subscriptionTypeId.
   */
  DUPLICATE_SUBSCRIPTION_TYPE_ID = 'DUPLICATE_SUBSCRIPTION_TYPE_ID',
  /**
   * This form has reCAPTCHA enabled so programatic submissions are not allowed.
   */
  FORM_HAS_RECAPTCHA_ENABLED = 'FORM_HAS_RECAPTCHA_ENABLED',

  /**
   * The HubSpot account has reached the rate limit.
   */
  ERROR_429 = 'ERROR 429',
}

export type HubSpotContext = {
  hutk?: string;
  pageUri?: string;
  pageName?: string;
};

type HubSpotField = {
  /**
   * Should usually be "0-1"
   *
   * Per HubSpot docs: https://developers.hubspot.com/docs/api/crm/crm-custom-objects#:~:text=When%20creating%20a%20new%20association%20between%20two%20custom%20objects%2C%20specify%20the%20custom%20objects%20by%20their%20objectTypeId%20in%20the%C2%A0toObjectTypeId%20field.%20For%20standard%20objects%2C%20you%20can%20identify%20them%20by%20name%20or%20use%20the%20following%20values%3A%C2%A0
   *
   * ```
   * When creating a new association between two custom objects, specify the custom objects by their objectTypeId in the toObjectTypeId field. For standard objects, you can identify them by name or use the following values:
   *
   * Contact: 0-1
   * Company: 0-2
   * Deal: 0-3
   * Ticket: 0-5
   * ```
   */
  objectTypeId: '0-1' | '0-2' | '0-3' | '0-5';
  name: string;
  value: string;
};

export interface FormSubmitRequest {
  /** in milliseconds */
  submittedAt?: string;
  fields: HubSpotField[];
  context?: HubSpotContext;
  legalConsentOptions?: {
    /**
     * Include this object when GDPR options are enabled
     */
    consent?: {
      consentToProcess: boolean;
      text: string;
      communications: {
        value: boolean;
        subscriptionTypeId: number;
        text: string;
      }[];
    };
  };
}

export interface FormSubmitResponse {
  /**
   * 	If the submission was accepted, and the form has a redirect URI set in its settings, this will be that redirect URI.

   */
  redirectUri: string;
  /**
   * If the submission was accepted, and the form has an inline thank you message set in its settings, this will the the HTML for that message.

   */
  inlineMessage: string;
  /**
   * A list of errors with the submission data.
   * Each entry will contain a message value detailing the specific error, and an coded errorType. You can find a list of errorType options below.
   */
  errors: { message: string; errorType: SubmitFormErrorCodes }[];
}

export type FormConfig = {
  region: string;
  portalId: string;
  formId: string;
};

export const FormConfigMap = {
  TestDriveForm: {
    region: 'na1',
    portalId: '8911712',
    formId: '9c8ad4d2-45ac-4eef-834d-a2cb1012eb7d',
  },
  RequestDemoForm: {
    region: 'na1',
    portalId: '8911712',
    formId: '48c90f22-c8ff-4b5d-8bfc-4c88e34b91e4',
  },
  TalkToSalesForm: {
    region: 'na1',
    portalId: '8911712',
    formId: 'ae5a8369-22fd-41c7-af8f-bbcd29dd9256',
  },
  ContentAIRequestForm: {
    region: 'na1',
    portalId: '8911712',
    formId: '41d96225-8499-4656-8d02-5674e2514844',
  },
  bookMeetingForm: {
    region: 'na1',
    portalId: '8911712',
    formId: 'fa8ead55-0732-405f-9735-e33a1405f2fd',
  },
  upgradeToolsPlanRequest: {
    region: 'na1',
    portalId: '8911712',
    formId: '49515322-c909-4718-ae3d-39bd70f30828',
  },
};

const HubSpotFieldMapping: Record<string, Optional<Omit<HubSpotField, 'value'>, 'objectTypeId'>> = {
  emailAddress: {
    name: 'email',
  },
  firstName: {
    name: 'firstname',
  },
  first_name: {
    name: 'firstname',
  },
  last_name: {
    name: 'lastname',
  },
  lastName: {
    name: 'lastname',
  },
  phoneNumber: {
    name: 'phone',
  },
  phone_number: {
    name: 'phone',
  },
  domain: {
    name: 'website',
  },
};

/**
 * Wrapper around the HubSpot API, to add the following:
 * - extra type information
 * - a simpler abstraction (for the eventual v3 upgrade)
 * - lazy initialization
 *
 * Safe to use in the front-end, as there are no secrets being passed
 */
class HubSpotService {
  private _axios: AxiosInstance | undefined = undefined;

  /**
   * use a getter for lazy init
   */
  get axios() {
    if (!this._axios) {
      this._axios = axios.create({
        baseURL: 'https://api.hsforms.com/submissions/v3/',
      });
    }

    return this._axios;
  }

  /**
   * For unit testing
   */
  set axios(newAxios: AxiosInstance) {
    this._axios = newAxios;
  }

  sendDataToForm = async (formConfig: FormConfig, data: Record<string, unknown>, context?: HubSpotContext) => {
    const formData = formatFormData(data, context);
    const result = await this.axios.post<FormSubmitResponse, AxiosResponse<FormSubmitResponse>, FormSubmitRequest>(
      `integration/submit/${formConfig.portalId}/${formConfig.formId}`,
      formData
    );

    return result.data;
  };
}

export const formatFormData = (data: Record<string, unknown>, context?: HubSpotContext): FormSubmitRequest => {
  const dataKeys = Object.keys(data);
  const fields = dataKeys
    .filter((key) => Boolean(data[key]))
    .map((key) => {
      const mapping = HubSpotFieldMapping[key];

      const name = mapping?.name || key;
      const objectTypeId = mapping?.objectTypeId || '0-1';

      return {
        name,
        objectTypeId,
        value: `${data[key]}`,
      };
    });

  const result: FormSubmitRequest = {
    submittedAt: `${new Date().getTime()}`,
    fields,
    // legalConsentOptions: {},
  };

  return result;
};

export default new HubSpotService();
